import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import {
  useGetIntegrationStatus,
  useGetDashboardSummary,
} from '@/api/generated/v2-shop/v2-shop'
import {
  useGetScriptTagStatus,
} from '@/api/generated/v2-scripttag/v2-scripttag'
import {
  useGetMyCapiEventStats,
} from '@/api/generated/v2-meta/v2-meta'
import {
  useListCampaigns,
} from '@/api/generated/v2-seeding-campaigns/v2-seeding-campaigns'
import type {
  IntegrationStatusResponse as IntegrationStatusResponse,
  ScriptTagStatusResponse,
  DashboardSummaryResponse,
  SeedingCampaignResponse as SeedingCampaignResponse,
} from '@/api/schemas'
import { HomeNotices } from '@/features/home/HomeNotices'
import { SHOW_LEGACY_UPGRADE_BADGE } from '@/lib/featureFlags'
import { useShop } from '@/lib/shopContext'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertCircle, CheckCircle, Lock } from 'lucide-react'

/** 이벤트 한국어 이름 매핑 */
const EVENT_KO: Record<string, string> = {
  PageView: '페이지 조회', ViewContent: '콘텐츠 조회', AddToCart: '장바구니 추가',
  InitiateCheckout: '결제 시작', Purchase: '구매 완료', Search: '검색',
  CompleteRegistration: '회원가입', Lead: '잠재고객', AddPaymentInfo: '결제정보 추가',
}
import { useMemo, useState } from 'react'

export const Route = createFileRoute('/_dashboard/')({
  component: Dashboard,
})

function Dashboard() {
  const navigate = useNavigate()
  const { shop, catalogConnected } = useShop()

  const [checklistDismissed, setChecklistDismissed] = useState(() =>
    localStorage.getItem('onboarding-checklist-dismissed') === 'true'
  )

  // === Orval Query 훅: 레이아웃에서 인증 + shop 보장됨 ===

  // 연동 상태 조회
  const { data: integrationRaw } = useGetIntegrationStatus()
  const integrationStatus = (integrationRaw ?? null) as IntegrationStatusResponse | null

  // 스크립트 설치 상태 조회
  const { data: scriptRaw } = useGetScriptTagStatus()
  const scriptStatus = (scriptRaw ?? null) as ScriptTagStatusResponse | null
  const scriptTagInstalled = scriptStatus?.installed ?? null

  // 대시보드 요약 데이터
  const { data: summaryRaw } = useGetDashboardSummary()
  const summary = (summaryRaw ?? null) as DashboardSummaryResponse | null

  // CAPI 이벤트 통계 (오늘 기준 7일) — 홈 카드용
  const todayStr = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])
  const weekAgoStr = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - 6)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])
  const isCapiConnected = !!(shop as any)?.meta_pixel_id && !!(shop as any)?.meta_capi_token_set
  const { data: capiStatsRaw } = useGetMyCapiEventStats(
    { start_date: weekAgoStr, end_date: todayStr },
    { query: { enabled: isCapiConnected } },
  )
  // 이벤트 타입별 집계
  const capiByEvent = useMemo(() => {
    const stats = (capiStatsRaw as { stats: { event_name: string; total_count: number }[] } | undefined)?.stats ?? []
    const map = new Map<string, number>()
    for (const s of stats) {
      map.set(s.event_name, (map.get(s.event_name) ?? 0) + s.total_count)
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [capiStatsRaw])

  // 체험단 캠페인 목록 조회
  const { data: campaignsRaw } = useListCampaigns()
  // 진행중/모집중 캠페인만 필터 (draft, completed, archived 제외)
  const activeCampaigns = useMemo(() => {
    const list = (campaignsRaw as unknown as { campaigns: SeedingCampaignResponse[] } | undefined)
      ?.campaigns ?? []
    const activeStatuses = ['recruiting', 'pending_selection', 'in_progress']
    return list.filter((c) => c.status && activeStatuses.includes(c.status))
  }, [campaignsRaw])

  // 보조 데이터 로드 완료 여부 — flash 방지
  const dataReady = !!shop && integrationRaw !== undefined && scriptRaw !== undefined

  if (!dataReady) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-on-surface-variant">대시보드 데이터 로딩 중...</p>
        </div>
      </div>
    )
  }

  // === 연동 상태 계산 ===
  const isMixpanelConnected = !!shop.mixpanel_token && !!integrationStatus?.mixpanel?.is_valid
  const isMetaCapiConnected = !!shop.meta_pixel_id && !!shop.meta_capi_token_set
  const isMetaAdsConnected = !!shop.meta_ad_account_id
  const isMetaCatalogConnected = catalogConnected
  // 전 기능 오픈 — 체험단 플래그 체크 없음

  const isScriptInstalled = !!scriptTagInstalled

  const formatDateTime = (dateStr: string) => {
    const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
    return new Date(utcDateStr).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
  }

  // === 레거시 연동 판별 (FLB 토큰 없이 자산 ID만 있는 상태) ===
  const isAdsLegacy = !shop.meta_flb_ads_connected && isMetaAdsConnected
  const isCatalogLegacy = !shop.meta_flb_catalog_connected && catalogConnected

  // === 토큰 회수 판별 (연동됨이지만 수집이 멈춘 상태) ===
  // "완료" 체크로 보여주면 정상이라 오인해 장애가 방치되므로 재연동을 유도한다.
  // (같은 신호를 HomeNotices 배너가 상단에서 더 자세히 안내한다)
  const isAdsTokenInvalid = !!shop.meta_flb_ads_token_invalid
  const isCatalogTokenInvalid = !!shop.meta_flb_catalog_token_invalid

  // === 연동 체크리스트 ===
  // 카페24 스크립트를 가장 앞에 두고, 방문자 분석/전환 API는 스크립트 설치 선행 필수.
  // (스크립트가 트래커 코드를 페이지에 주입하므로, 데이터 수집을 쓰는 두 연동의 전제조건)
  // 카탈로그/광고계정은 백엔드 API 연동이라 스크립트와 독립적이므로 선행 조건 없음.
  // 라벨은 사이드바와 통일 — '카페24 스크립트' / '방문자 분석' / '전환 API' / '카탈로그' / '광고계정 연동'
  const scriptBlockedReason = '스크립트를 먼저 설치해주세요'
  const setupSteps: { label: string; done: boolean; to: string; blocked?: boolean; blockedReason?: string; legacy?: boolean; tokenInvalid?: boolean }[] = [
    { label: '카페24 스크립트', done: isScriptInstalled, to: '/setup/script' },
    {
      label: '방문자 분석', done: isMixpanelConnected, to: '/setup/mixpanel',
      blocked: !isScriptInstalled,
      blockedReason: scriptBlockedReason,
    },
    {
      label: '전환 API', done: isMetaCapiConnected, to: '/setup/meta-capi',
      blocked: !isScriptInstalled,
      blockedReason: scriptBlockedReason,
    },
    {
      label: '카탈로그', done: isMetaCatalogConnected, to: '/setup/meta-catalog',
      legacy: isCatalogLegacy, tokenInvalid: isCatalogTokenInvalid,
    },
    {
      // 캠퍼스 통합 이후 광고 계정만 단독 연동하는 케이스가 있어 선행 조건 가드 없음.
      // (광고 데이터 자동 수집은 백엔드에서 Mixpanel 토큰 없으면 자동 skip)
      label: '광고계정 연동', done: isMetaAdsConnected, to: '/setup/meta-ads',
      legacy: isAdsLegacy, tokenInvalid: isAdsTokenInvalid,
    },
    // 구글 광고 — 허용 목록 샵에만 칸이 생긴다(목록 밖 샵은 지금처럼 5칸). 계정까지 골라야 완료.
    // 권한 누락도 "망가짐" 으로 센다 — 연동됨이라면서 수집이 안 되는 상태다.
    ...(shop.google_enabled && shop.google
      ? [
          {
            label: '구글 광고',
            done: shop.google.connected && shop.google.account_selected,
            to: '/setup/google',
            tokenInvalid:
              shop.google.credential_invalid || shop.google.missing_scopes.length > 0,
          },
        ]
      : []),
  ]
  // 토큰이 죽은 항목은 카운트에서 뺀다 — "5/5"라면서 그중 하나가 빨강
  // "재연동 필요" 카드면 메시지가 서로 모순된다
  const doneCount = setupSteps.filter((s) => s.done && !s.tokenInvalid).length
  const allDone = doneCount === setupSteps.length
  // 레거시(직접입력) 연동은 done 이어도 FLB 간편연동 전환을 유도해야 하므로,
  // 모든 연동이 완료됐어도 체크리스트를 접지 않고 계속 노출한다.
  const hasLegacyUpgrade =
    SHOW_LEGACY_UPGRADE_BADGE && setupSteps.some((s) => s.legacy)
  // 토큰이 죽은 항목이 있으면 체크리스트를 계속 노출한다 — 카운터를 깎아놓고
  // 정작 카드를 감추면 사용자가 무엇이 문제인지 볼 수 없다
  const showChecklist =
    dataReady &&
    !checklistDismissed &&
    (setupSteps.some((s) => !s.done || s.tokenInvalid) || hasLegacyUpgrade)

  const dismissChecklist = () => {
    setChecklistDismissed(true)
    localStorage.setItem('onboarding-checklist-dismissed', 'true')
  }

  // === 메인 대시보드 렌더링 ===
  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* 확인이 필요한 항목 — 설정했는데 망가짐/조치 필요 (문제 없으면 미렌더) */}
      <HomeNotices shop={shop} catalogSyncStatus={summary?.catalog?.last_sync_status} />

      {/* 온보딩 체크리스트 — 접기/펼치기 가능 */}
      {showChecklist && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-on-surface">
              연동 설정
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-outline-variant">
                {doneCount}/{setupSteps.length}
              </span>
              <button
                onClick={dismissChecklist}
                className="text-xs text-on-surface-variant hover:text-on-surface border border-border rounded px-2 py-0.5 transition-colors"
              >
                접기
              </button>
            </div>
          </div>
          {/* 구글 칸이 붙는 샵(허용 목록)은 6칸 — 5열이면 한 칸만 다음 줄로 떨어진다.
              Tailwind 가 읽도록 클래스 문자열을 통째로 쓴다(조각 조립 금지). */}
          <div
            className={
              setupSteps.length > 5
                ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'
                : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3'
            }
          >
            {setupSteps.map((step, index) => {
              // 완료: 체크마크 + 연한 배경
              if (step.done) {
                // 레거시(수동/SU) 연동은 "완료"지만 FLB 간편 연동으로의 업그레이드를
                // 권장한다. 배지 + amber 테두리/배경으로 시각적으로 강조한다.
                const isLegacyUpgrade = SHOW_LEGACY_UPGRADE_BADGE && step.legacy
                // 토큰 회수는 "연동됨이지만 수집이 멈춘" 상태 — 완료 체크로 보이면
                // 정상이라 오인하므로 경고로 대체한다 (레거시 배지보다 우선).
                const isTokenInvalid = !!step.tokenInvalid
                return (
                  <Link
                    key={step.to}
                    to={step.to}
                    className={`rounded-2xl p-4 flex flex-col items-center gap-2 text-center transition-colors ${
                      isTokenInvalid
                        ? 'bg-red-50/60 border border-red-300 hover:bg-red-50'
                        : isLegacyUpgrade
                          ? 'bg-amber-50/60 border border-amber-300 hover:bg-amber-50'
                          : 'bg-surface-lowest border border-border hover:bg-surface-container'
                    }`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        isTokenInvalid ? 'bg-red-50' : 'bg-green-50'
                      }`}
                    >
                      {isTokenInvalid ? (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-on-surface-variant">
                      {step.label}
                    </span>
                    {isTokenInvalid ? (
                      <span className="text-xs text-red-600 font-medium">
                        재연동 필요
                      </span>
                    ) : isLegacyUpgrade ? (
                      <span className="text-xs text-amber-600 font-medium">
                        간편연동으로 전환
                      </span>
                    ) : (
                      <span className="text-xs text-green-600 font-medium">
                        완료
                      </span>
                    )}
                  </Link>
                )
              }

              // 잠김: 자물쇠 + 안내 문구 (툴팁 대신 카드 본문에 직접 노출 — 즉시 인지 가능)
              if (step.blocked) {
                return (
                  <div
                    key={step.to}
                    className="bg-surface-lowest border border-border rounded-2xl p-4 flex flex-col items-center gap-2 text-center cursor-not-allowed"
                  >
                    <div className="h-8 w-8 rounded-full bg-surface-container flex items-center justify-center">
                      <Lock className="h-4 w-4 text-on-surface-variant" />
                    </div>
                    <span className="text-sm font-medium text-on-surface-variant">
                      {step.label}
                    </span>
                    <span className="text-xs text-on-surface-variant">
                      스크립트를 먼저 설치해주세요
                    </span>
                  </div>
                )
              }

              // 진행 필요: 강조 테두리 + CTA 버튼
              // 첫 번째 미완료 + 비잠김 스텝을 강조 (파란 테두리)
              // 카드 헤더는 빈 원형 placeholder — 완료/잠김 카드와 높이를 맞추기 위함.
              // (순서 번호는 표시하지 않음. 사용자가 필요한 것만 골라 설정하는 흐름)
              const isNext = index === setupSteps.findIndex((s) => !s.done && !s.blocked)
              return (
                <Link
                  key={step.to}
                  to={step.to}
                  className={`bg-surface-lowest rounded-2xl p-4 flex flex-col items-center gap-2 text-center transition-colors hover:bg-surface-container ${
                    isNext
                      ? 'border-2 border-primary ring-1 ring-primary/20'
                      : 'border border-border'
                  }`}
                >
                  <div className="h-8 w-8 rounded-full border-2 border-border" />
                  <span className="text-sm font-medium text-on-surface">
                    {step.label}
                  </span>
                  <span className="text-xs text-primary font-semibold">
                    설정하기
                  </span>
                </Link>
              )
            })}
          </div>
        </section>
      )}

      {/* 체크리스트 접힌 상태 — 미완료 항목이 있으면 펼치기 버튼 */}
      {dataReady && checklistDismissed && (setupSteps.some((s) => !s.done) || hasLegacyUpgrade) && (
        <button
          type="button"
          onClick={() => { setChecklistDismissed(false); localStorage.removeItem('onboarding-checklist-dismissed') }}
          className="w-full text-left px-4 py-3 bg-surface-lowest border border-border rounded-lg text-sm text-on-surface-variant hover:bg-surface transition-colors flex items-center justify-between"
        >
          <span>연동 설정 <span className="text-xs text-outline-variant ml-1">{doneCount}/{setupSteps.length} 완료</span></span>
          <span className="text-xs text-primary">펼치기</span>
        </button>
      )}

      {/* 상태 카드 3열 그리드 */}
      {dataReady && (summary?.capi || summary?.catalog || summary?.seeding) && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* CAPI 이벤트 현황 — 이벤트 타입별 breakdown */}
          {summary?.capi && ((summary.capi.last_24h_success ?? 0) > 0 || (summary.capi.last_24h_failed ?? 0) > 0) && (
            <div className="bg-surface-lowest rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-on-surface">전환 이벤트</h3>
                <Link to="/capi" className="text-xs text-outline-variant hover:text-on-surface-variant">
                  상세 &rarr;
                </Link>
              </div>
              {/* 24시간 요약 */}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-bold text-on-surface">
                  {(summary.capi.last_24h_success ?? 0).toLocaleString()}
                </span>
                <span className="text-xs text-on-surface-variant">24시간</span>
                {(summary.capi.last_24h_failed ?? 0) > 0 && (
                  <span className="text-xs text-error ml-1">
                    실패 {(summary.capi.last_24h_failed ?? 0)}
                  </span>
                )}
              </div>
              <p className="text-xs text-on-surface-variant mb-3">
                주간 {(summary.capi.last_7d_success ?? 0).toLocaleString()}건
              </p>
              {/* 이벤트 타입별 breakdown (7일 기준) */}
              {capiByEvent.length > 0 && (
                <div className="border-t border-border pt-3 space-y-1.5">
                  <p className="text-xs text-outline-variant mb-1">주간 이벤트별</p>
                  {capiByEvent.map(({ name, count }) => (
                    <div key={name} className="flex items-center justify-between text-xs">
                      <span className="text-on-surface-variant">{EVENT_KO[name] || name}</span>
                      <span className="font-medium text-on-surface tabular-nums">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 카탈로그 동기화 현황 */}
          {summary?.catalog && (
            <div className="bg-surface-lowest rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-on-surface">카탈로그 동기화</h3>
                <Link to="/catalog" className="text-sm text-outline-variant hover:text-on-surface-variant">
                  상품 관리 &rarr;
                </Link>
              </div>
              <div className="space-y-3">
                {/* 동기화 상태 표시 */}
                <div className="flex items-center gap-1.5">
                  {summary.catalog.last_sync_status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                  {summary.catalog.last_sync_status === 'failed' && <AlertCircle className="h-4 w-4 text-red-500" />}
                  {summary.catalog.last_sync_status === 'running' && (
                    <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  )}
                  <span className="text-sm font-medium text-on-surface">
                    {summary.catalog.last_sync_status === 'completed' ? '정상' :
                     summary.catalog.last_sync_status === 'failed' ? '실패' :
                     summary.catalog.last_sync_status === 'running' ? '진행 중' :
                     summary.catalog.last_sync_status === 'completed_with_errors' ? '부분 실패' :
                     summary.catalog.last_sync_status}
                  </span>
                </div>
                {summary.catalog.last_sync_at && (
                  <p className="text-sm text-outline-variant">
                    {formatDateTime(summary.catalog.last_sync_at)}
                  </p>
                )}
                {summary.catalog.synced_products != null && (
                  <div>
                    <p className="text-2xl font-bold text-on-surface">
                      {summary.catalog.synced_products.toLocaleString()}
                    </p>
                    <p className="text-xs text-on-surface-variant">동기화된 상품</p>
                  </div>
                )}
                {summary.catalog.failed_products != null && summary.catalog.failed_products > 0 && (
                  <div>
                    <p className="text-lg font-semibold text-error">
                      {summary.catalog.failed_products.toLocaleString()}
                    </p>
                    <p className="text-xs text-on-surface-variant">실패</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 체험단 현황 — 진행 요약만 표시 (상세는 아래 캠페인 카드에서) */}
          {summary?.seeding && ((summary.seeding.recruiting ?? 0) > 0 || (summary.seeding.in_progress ?? 0) > 0) && (
            <div className="bg-surface-lowest rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-on-surface">체험단</h3>
                <Link to="/seeding-campaigns" className="text-sm text-outline-variant hover:text-on-surface-variant">
                  캠페인 관리 &rarr;
                </Link>
              </div>
              <div className="space-y-3">
                {/* 모집중 + 진행중 합산 요약 */}
                <div className="flex items-baseline gap-3">
                  {(summary.seeding.recruiting ?? 0) > 0 && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold text-primary">{summary.seeding.recruiting}</span>
                      <span className="text-xs text-on-surface-variant">모집중</span>
                    </div>
                  )}
                  {(summary.seeding.recruiting ?? 0) > 0 && (summary.seeding.in_progress ?? 0) > 0 && (
                    <span className="text-on-surface-variant">·</span>
                  )}
                  {(summary.seeding.in_progress ?? 0) > 0 && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold text-green-600">{summary.seeding.in_progress}</span>
                      <span className="text-xs text-on-surface-variant">진행중</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 체험단 진행 현황 — 진행중/모집중 캠페인 개별 카드 + 새 캠페인 버튼 통합 */}
      {dataReady && activeCampaigns.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-on-surface">체험단 진행 현황</h2>
            <div className="flex items-center gap-3">
              <Link to="/seeding-campaigns/new" className="text-sm text-primary hover:text-primary-dim transition-colors">
                + 새 캠페인
              </Link>
              {activeCampaigns.length > 5 && (
                <Link to="/seeding-campaigns" className="text-sm text-outline-variant hover:text-on-surface-variant transition-colors">
                  전체 보기 &rarr;
                </Link>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {activeCampaigns.slice(0, 5).map((campaign) => (
              <ActiveCampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        </section>
      )}

      {/* 모든 연동 완료 + 데이터 없을 때 — 다음 행동 안내 */}
      {dataReady && allDone && !hasLegacyUpgrade && !summary?.capi && !summary?.catalog && !summary?.seeding && (
        <div className="text-center py-16">
          <div className="text-4xl mb-4">🎉</div>
          <h3 className="text-base font-semibold text-on-surface mb-2">
            모든 ��동이 완료되었어요!
          </h3>
          <p className="text-on-surface-variant text-sm mb-6">
            데이터가 수집되면 여기에 현황이 표시돼요.
            <br />
            그동안 체험단 캠페인을 만들어보는 건 어떨까요?
          </p>
          <Link
            to="/seeding-campaigns/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dim transition-colors text-sm font-medium"
          >
            첫 캠페인 만들기
          </Link>
        </div>
      )}
    </div>
  )
}

// === 캠페인 상태 뱃지 매핑 ===
const STATUS_BADGE: Record<string, { variant: 'info' | 'warning' | 'success'; label: string }> = {
  recruiting: { variant: 'info', label: '모집중' },
  pending_selection: { variant: 'warning', label: '선정대기' },
  in_progress: { variant: 'success', label: '진행중' },
}

/** D-day 문자열 계산 (양수: D-N, 0: D-Day, 음수: D+N) */
function formatDday(targetDate: string | null | undefined): string | null {
  if (!targetDate) return null
  const now = new Date()
  const target = new Date(targetDate.endsWith('Z') ? targetDate : targetDate + 'Z')
  const diffMs = target.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays > 0) return `D-${diffDays}`
  if (diffDays === 0) return 'D-Day'
  return `D+${Math.abs(diffDays)}`
}

/** 진행중/모집중 캠페인 개별 카드 */
function ActiveCampaignCard({ campaign }: { campaign: SeedingCampaignResponse }) {
  const status = campaign.status ?? 'recruiting'
  const badge = STATUS_BADGE[status] ?? STATUS_BADGE.recruiting

  // D-day: 모집중 → 모집 마감, 진행중 → 미션 마감 또는 캠페인 종료
  const dday = status === 'recruiting'
    ? formatDday(campaign.recruit_end_at)
    : formatDday(campaign.mission_deadline_at ?? campaign.campaign_end_at)

  // 진행률 계산
  const isRecruiting = status === 'recruiting' || status === 'pending_selection'
  const current = isRecruiting
    ? (campaign.application_count ?? 0)
    : (campaign.mission_submitted_count ?? 0)
  // 모집 인원은 draft 에서 비어 있을 수 있다(임시 저장). 이 카드는 공개된 캠페인만
  // 다루지만, 타입상 null 이 가능하므로 0 으로 접어 진행률 계산이 NaN 이 되지 않게 한다.
  const total = isRecruiting
    ? (campaign.recruit_count ?? 0)
    : (campaign.selected_count ?? campaign.recruit_count ?? 0)
  const progressPct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0
  const progressLabel = isRecruiting
    ? `신청 ${current}/${total}명`
    : `미션 제출 ${current}/${total}건`

  return (
    <Link
      to="/seeding-campaigns/$campaignId"
      params={{ campaignId: campaign.id }}
      className="flex items-center gap-3 bg-surface-lowest rounded-2xl border border-border p-4 hover:bg-surface-container transition-colors"
    >
      {/* 썸네일 */}
      {campaign.thumbnail_url ? (
        <img
          src={campaign.thumbnail_url}
          alt=""
          className="w-10 h-10 rounded-lg object-cover shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-surface-container shrink-0 flex items-center justify-center">
          <span className="text-xs text-outline-variant">N/A</span>
        </div>
      )}

      {/* 본문 */}
      <div className="flex-1 min-w-0">
        {/* 제목 */}
        <p className="text-sm font-medium text-on-surface truncate">{campaign.title}</p>

        {/* 상태 뱃지 + D-day */}
        <div className="flex items-center gap-2 mt-1">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {dday && (
            <span className="text-xs text-on-surface-variant">{dday}</span>
          )}
        </div>

        {/* 프로그레스 */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-on-surface-variant">{progressLabel}</span>
            <span className="text-xs text-outline-variant">{progressPct}%</span>
          </div>
          <div className="w-full h-1.5 bg-surface-container rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isRecruiting ? 'bg-primary' : 'bg-green-500'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>
    </Link>
  )
}
